import {
  ChevronUpDownIcon,
  ComputerDesktopIcon,
  MoonIcon,
  SunIcon,
} from '@heroicons/react/24/outline';
import {
  RichToggle,
  type RichToggleOption,
} from '@sparklayer-io/spark-dash-ui/components/rich-toggle';
import type { ColorSchemePreference } from '@sparklayer-io/spark-dash-ui/lib/color-scheme';
import { cn } from '@sparklayer-io/spark-dash-ui/lib/styles';
import type { ComponentType, ReactNode } from 'react';
import { acronymOf } from './helpers';

const THEME_OPTIONS: RichToggleOption<ColorSchemePreference>[] = [
  { value: 'light', label: 'Light', icon: <SunIcon className="size-3.5 shrink-0" /> },
  { value: 'dark', label: 'Dark', icon: <MoonIcon className="size-3.5 shrink-0" /> },
  { value: 'system', label: 'System', icon: <ComputerDesktopIcon className="size-3.5 shrink-0" /> },
];

type SidebarActionLinkProps = {
  href: string;
  label: string;
  icon?: ComponentType<{ className?: string }>;
  iconNode?: ReactNode;
  active?: boolean;
  external?: boolean;
  onNavigate?: (event: React.MouseEvent, href: string) => void;
};

export function SidebarActionLink({
  href,
  label,
  icon: Icon,
  iconNode,
  active,
  external,
  onNavigate,
}: SidebarActionLinkProps) {
  return (
    <a
      href={href}
      onClick={onNavigate ? (event) => onNavigate(event, href) : undefined}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      className={cn(
        'group flex items-center rounded-md border border-transparent p-1.5 font-medium text-sm transition-all hover:bg-spark-surface-hover hover:text-spark-accent focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-spark-accent-hover focus-visible:ring-offset-2 focus-visible:ring-offset-spark-surface-subtle',
        active && 'border-spark-accent-border! bg-spark-accent-soft text-spark-accent',
      )}
    >
      {iconNode ?? (Icon && <Icon className="mr-2 size-4 group-hover:text-spark-primary-accent" />)}
      <span>{label}</span>
    </a>
  );
}

type UserMenuLinkProps = {
  href: string;
  label: string;
  icon?: ComponentType<{ className?: string }>;
  active?: boolean;
  onClick: (event: React.MouseEvent) => void;
};

export function UserMenuLink({ href, label, icon: Icon, active, onClick }: UserMenuLinkProps) {
  return (
    <a
      href={href}
      onClick={onClick}
      className={cn(
        'flex items-center rounded-md border border-transparent px-3 py-1.5 text-sm text-spark-text-primary transition-all hover:bg-spark-surface-hover hover:text-spark-accent',
        active && 'border-spark-accent-border! bg-spark-accent-soft text-spark-accent',
      )}
      role="menuitem"
    >
      {Icon && <Icon className="mr-2 size-4 shrink-0" />}
      {label}
    </a>
  );
}

type UserMenuButtonProps = {
  name: string;
  email: string;
  onClick: () => void;
};

export function UserMenuButton({ name, email, onClick }: UserMenuButtonProps) {
  return (
    <button
      type="button"
      className="group flex w-full cursor-pointer items-center gap-3 rounded-lg border border-spark-accent-border bg-spark-accent-soft px-3 py-2 focus:outline-hidden focus:ring-2 focus:ring-spark-accent-hover focus:ring-offset-2 focus:ring-offset-spark-surface-subtle"
      onClick={onClick}
    >
      <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-spark-accent-solid font-semibold text-spark-text-inverse text-xs">
        {acronymOf(name)}
      </div>
      <div className="min-w-0 flex-1 text-left">
        <p className="truncate font-medium text-sm text-spark-text-primary">{name}</p>
        <p className="truncate text-spark-text-secondary text-xs">{email}</p>
      </div>
      <ChevronUpDownIcon className="size-4.5 shrink-0 stroke-[1.5] text-spark-text-tertiary group-hover:text-spark-text-secondary" />
    </button>
  );
}

type UserMenuThemeSelectorProps = {
  value: ColorSchemePreference;
  onChange: (preference: ColorSchemePreference) => void;
};

export function UserMenuThemeSelector({ value, onChange }: UserMenuThemeSelectorProps) {
  return (
    <RichToggle size="sm" label="Theme" value={value} options={THEME_OPTIONS} onChange={onChange} />
  );
}
